import {
  tenantApiRoutes,
  OrganizationResponse,
  TenantProfileResponse,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  CreateTenantProfileDto,
  UpdateTenantProfileDto,
} from '@repo/contracts/tenant-api';

import { tenantApi } from '@/redux/api/tenant-api';

/**
 * RTK Query API slice for organizations endpoints
 */
export const organizationsApi = tenantApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    /**
     * GET /organization - Get current organization
     */
    getCurrentOrganization: builder.query<OrganizationResponse, void>({
      query: () => tenantApiRoutes.organizations.current(),
      providesTags: ['Organization'],
    }),

    /**
     * GET /organization/all - List all organizations
     */
    getAllOrganizations: builder.query<OrganizationResponse[], void>({
      query: () => tenantApiRoutes.organizations.list(),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: 'Organization' as const,
                id,
              })),
              { type: 'Organization', id: 'LIST' },
            ]
          : [{ type: 'Organization', id: 'LIST' }],
    }),

    /**
     * GET /tenant - Get organization group
     */
    getTenantProfile: builder.query<TenantProfileResponse, void>({
      query: () => tenantApiRoutes.tenantProfile.get(),
      providesTags: ['TenantProfile'],
    }),

    /**
     * GET /organizations/:id - Get organization by ID
     */
    getOrganizationById: builder.query<OrganizationResponse, string>({
      query: (id) => tenantApiRoutes.organizations.getById(id),
      providesTags: (result, error, id) => [{ type: 'Organization', id }],
    }),

    /**
     * POST /organizations - Create new organization
     */
    createOrganization: builder.mutation<
      OrganizationResponse,
      CreateOrganizationDto
    >({
      query: (body) => ({
        url: tenantApiRoutes.organizations.create(),
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Organization', id: 'LIST' }],
    }),

    /**
     * POST /organization - Create current organization
     */
    createCurrentOrganization: builder.mutation<
      OrganizationResponse,
      CreateOrganizationDto
    >({
      query: (body) => ({
        url: tenantApiRoutes.organizations.createCurrent(),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organization', { type: 'Organization', id: 'LIST' }],
    }),

    /**
     * PATCH /organization - Update current organization
     */
    updateCurrentOrganization: builder.mutation<
      OrganizationResponse,
      UpdateOrganizationDto
    >({
      query: (data) => ({
        url: tenantApiRoutes.organizations.updateCurrent(),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Organization', { type: 'Organization', id: 'LIST' }],
    }),

    /**
     * PATCH /organizations/:id - Update organization
     */
    updateOrganization: builder.mutation<
      OrganizationResponse,
      { id: string; data: UpdateOrganizationDto }
    >({
      query: ({ id, data }) => ({
        url: tenantApiRoutes.organizations.update(id),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Organization', id },
        { type: 'Organization', id: 'LIST' },
      ],
    }),

    /**
     * DELETE /organizations/:id - Delete organization
     */
    deleteOrganization: builder.mutation<void, string>({
      query: (id) => ({
        url: tenantApiRoutes.organizations.delete(id),
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Organization', id },
        { type: 'Organization', id: 'LIST' },
      ],
    }),

    /**
     * POST /tenant - Create new organization group
     */
    createTenantProfile: builder.mutation<
      TenantProfileResponse,
      CreateTenantProfileDto
    >({
      query: (body) => ({
        url: tenantApiRoutes.tenantProfile.create(),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TenantProfile'],
    }),

    /**
     * PATCH /tenant/:id - Update organization group
     */
    updateTenantProfile: builder.mutation<
      TenantProfileResponse,
      { id: string; data: UpdateTenantProfileDto }
    >({
      query: ({ id, data }) => ({
        url: tenantApiRoutes.tenantProfile.update(id),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['TenantProfile'],
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetCurrentOrganizationQuery,
  useGetAllOrganizationsQuery,
  useGetTenantProfileQuery,
  useGetOrganizationByIdQuery,
  useCreateOrganizationMutation,
  useCreateCurrentOrganizationMutation,
  useUpdateCurrentOrganizationMutation,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useCreateTenantProfileMutation,
  useUpdateTenantProfileMutation,
} = organizationsApi;
